import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Activity as ActivityIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ActivityFeedItem } from '../components/activity/ActivityFeedItem';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 50;

/**
 * ActivityPage — full-page timeline (host or admin).
 * Cursor-paginated via `before=<iso>`. Purely presentational aside from fetch.
 */
export default function ActivityPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/activity`, {
        params: { limit: PAGE_SIZE },
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data?.items || []);
      setCursor(res.data?.next_cursor || null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load activity');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/activity`, {
        params: { limit: PAGE_SIZE, before: cursor },
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems((prev) => [...prev, ...(res.data?.items || [])]);
      setCursor(res.data?.next_cursor || null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load more activity');
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, loadingMore]);

  useEffect(() => { loadInitial(); }, [loadInitial]);

  return (
    <div className="min-h-screen bg-[#050505] text-[#EAE7E1]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-[#AFAFAF] hover:text-[#D4AF37] h-9 px-2"
            data-testid="activity-page-back"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <ActivityIcon className="w-5 h-5 text-[#D4AF37]" />
            <h1 className="text-xl font-semibold tracking-tight">Activity</h1>
          </div>
        </div>

        <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm" data-testid="activity-page-list">
          {loading && (
            <div className="px-4 py-12 text-center text-xs text-[#555]" data-testid="activity-page-loading">
              Loading activity…
            </div>
          )}
          {!loading && error && (
            <div className="px-4 py-12 text-center text-xs text-[#C44545]" data-testid="activity-page-error">{error}</div>
          )}
          {!loading && !error && items.length === 0 && (
            <div className="px-4 py-16 text-center" data-testid="activity-page-empty">
              <p className="text-sm text-[#777]">No activity yet</p>
            </div>
          )}
          {!loading && !error && items.length > 0 && (
            <>
              {items.map((it) => (
                <ActivityFeedItem
                  key={it.id}
                  item={it}
                  showEventTitle
                  onNavigate={(link) => navigate(link)}
                />
              ))}
              <div className="px-4 py-4 text-center border-t border-[#111]">
                {cursor ? (
                  <Button
                    onClick={loadMore}
                    variant="outline"
                    disabled={loadingMore}
                    className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#D4AF37] rounded-sm text-xs"
                    data-testid="activity-page-load-more"
                  >
                    {loadingMore ? 'Loading…' : 'Load more'}
                  </Button>
                ) : (
                  <p className="text-[11px] text-[#555]">End of activity</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
